import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { extractAadstsCode, trackAuthEvent } from '../observability/authTelemetry';
import {
  unsafeDecodeTeamsUserFromToken,
  detectTeamsContext,
  getTeamsAuthTokenSilent,
  getTeamsUserContext,
  type TeamsUserContext as TeamsUser,
} from '../teams';

// Single source of truth for the Teams silent-SSO probe state. Previously
// AuthGuard and useAuth each ran their own probe on mount (two
// auth.silent_sso_attempt events per session, two Teams SDK getAuthToken
// calls). The provider runs the probe exactly once; consumers read the
// resolved state.
//
// null = probe still in flight; true/false = settled.
export type TeamsSsoState = {
  inTeams: boolean | null;
  ready: boolean | null;
  user: TeamsUser | null;
};

const Context = createContext<TeamsSsoState>({ inTeams: null, ready: null, user: null });

export function TeamsSsoProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TeamsSsoState>({
    inTeams: null,
    ready: null,
    user: null,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const ctx = await detectTeamsContext();
      if (cancelled) return;
      const inside = ctx === 'in-teams';

      if (!inside) {
        setState({ inTeams: false, ready: false, user: null });
        return;
      }

      // Probe: does Teams silent SSO work right now? One call, no retry.
      // A success means: domain is tenant-verified, admin consent granted,
      // Teams client pre-authorised. Any failure => fall back to popup.
      const started = Date.now();
      trackAuthEvent('auth.silent_sso_attempt', { inTeams: true });
      try {
        const token = await getTeamsAuthTokenSilent();
        if (cancelled) return;
        let user = await getTeamsUserContext();
        if (!user) {
          // Teams can return a stripped ctx.user for "External unfamiliar" or
          // cross-tenant embeds. Decode the token claims as a fallback so the
          // header identity (greeting, user menu) still renders.
          user = unsafeDecodeTeamsUserFromToken(token);
          if (cancelled) return;
          trackAuthEvent(
            user ? 'auth.teams_user_from_token' : 'auth.teams_user_unresolved',
            { inTeams: true },
          );
        }
        if (cancelled) return;
        setState({ inTeams: true, ready: true, user });
        trackAuthEvent('auth.silent_sso_success', {
          inTeams: true,
          duration_ms: Date.now() - started,
        });
      } catch (err) {
        if (cancelled) return;
        setState({ inTeams: true, ready: false, user: null });
        const message = err instanceof Error ? err.message : String(err);
        const errorSubcode = extractAadstsCode(message);
        trackAuthEvent('auth.silent_sso_failure', {
          inTeams: true,
          duration_ms: Date.now() - started,
          errorCode: message.slice(0, 120),
          errorSubcode,
        });
        if (errorSubcode === 'AADSTS65001') {
          trackAuthEvent('auth.admin_consent_required', { inTeams: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return <Context.Provider value={state}>{children}</Context.Provider>;
}

// Co-located with the Provider on purpose — it's a 2-line `useContext` and
// splitting it into its own file just to appease react-refresh's Fast Refresh
// heuristic would trade a dev-only HMR quirk for more file surface and a
// cross-file export of the private Context. Prod builds aren't affected.
// eslint-disable-next-line react-refresh/only-export-components
export function useTeamsSso(): TeamsSsoState {
  return useContext(Context);
}
